import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import {
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
    AppScope,
} from '@klickdata/core/resource';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import * as moment from 'moment';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { ResourceBuilderComponent } from '../resource-builder/resource-builder.component';
import { CreateResourceBaseDirective } from '../resource-create/create-resource-base.directive';

@Component({
    selector: 'app-package-ecourse',
    templateUrl: './package-ecourse.component.html',
    styleUrls: ['./package-ecourse.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageEcourseComponent
    extends CreateResourceBaseDirective
    implements OnInit, AfterViewInit, OnDestroy, OnChanges, CanComponentDeactivate
{
    @Input() resource: Resource;
    @ViewChild(ResourceBuilderComponent) resourceBuilder: ResourceBuilderComponent;
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() saved: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Output() published: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Input() bottomToolSheet: boolean;
    public packageForm: FormGroup;
    public formats: { [key: string]: string };
    ResourceTypes = ResourceTypes;
    @Input() publishOnly: boolean;
    private publish: boolean;
    public isAdmin$: Observable<boolean>;
    public savingQuestions: boolean;
    AppScope = AppScope;

    constructor(
        protected fb: FormBuilder,
        protected auth: AuthService,
        public router: Router,
        protected route: ActivatedRoute,
        protected categoryService: ResourceCategoryService,
        protected messageService: MessageService,
        protected resourceService: ResourceService,
        protected cdRef: ChangeDetectorRef,
        protected dialog: MatDialog
    ) {
        super(messageService, resourceService, cdRef, dialog);
        this.buildForm();
        this.bottomToolSheet = this.bottomToolSheet || this.route.snapshot.data.bottomToolSheet;
    }

    ngOnInit() {
        this.isAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isAdmin())
        );
    }

    ngOnChanges() {
        if (this.resource) {
            this.update();
        }
    }
    public update() {
        this.packageForm.patchValue({
            id: this.resource.id,
            title: this.resource.title,
            media_id: this.resource.media_id,
            description: this.resource.description,
            priority: this.resource.priority,
            // category_ids: this.resource.category_ids,
            language_id: this.resource.language_id,
        });
        this.cdRef.markForCheck();
        FormHelper.resetForm(this.packageForm);
    }

    ngAfterViewInit() {
        this.resourceBuilder
            .getResource()
            .pipe(
                filter((resource) => !!resource),
                takeUntil(this.destroy)
            )
            .subscribe((resource) => this.updateForm(resource));
        this.resourceBuilder
            .getService()
            .isLoading()
            .pipe(takeUntil(this.destroy))
            .subscribe((loading) => (this.isLoading = loading));
    }

    updateForm(resource: Resource): void {
        this.packageForm.patchValue({
            id: this.packageForm.value.id ? this.packageForm.value.id : resource.id,
            title: this.packageForm.value.title ? this.packageForm.value.title : resource.title,
        });
        FormHelper.resetForm(this.packageForm);
    }

    public buildForm() {
        this.packageForm = this.fb.group({
            id: [],
            priority: [],
            title: ['', Validators.required],
            description: [''],
            media_id: [''],
            tag_ids: [[]],
            category_ids: [[], Validators.required],
            language_id: ['', Validators.required],
        });

        this.formats = {
            title: $localize`:@@title:Title`,
            description: $localize`:@@description:Description`,
            category_ids: $localize`:@@categories:Categories`,
        };
    }
    /**
     * Save/Publish button action.
     */
    public onSubmit(publish?: boolean) {
        this.publish = publish;
        const itemsReady = this.resourceBuilder.getService().readyForSubmit();
        if (!itemsReady) {
            if (publish) {
                this.messageService.openMessage(MessageErrorComponent, $localize`Questions is required!`);
            } else if (this.packageForm.dirty) {
                this.submitWithQuestion();
            }
        } else {
            this.savingQuestions = true;
            this.resourceBuilder.getService().submit();
        }
    }

    /**
     * Submit Survey after question saved event emitted from RB!
     */
    public submitWithQuestion() {
        this.savingQuestions = false;
        if (this.packageForm.invalid) {
            FormHelper.markForm(this.packageForm);
            this.messageService.openMessage(
                MessageErrorComponent,
                $localize`${FormHelper.formatInvalidForm(this.packageForm, this.formats)} is missing`
            );
            this.setLoading(false);
        } else {
            this.setLoading(true);
            this.prepareForm()
                .pipe(
                    takeUntil(this.destroy),
                    switchMap((data) =>
                        !data.id ? this.resourceService.store(data) : this.resourceService.update(data)
                    )
                )
                .subscribe((resource) => {
                    this.setLoading(false);
                    this.onSaved(resource);
                    this.cdRef.markForCheck();
                });
        }
    }

    private onSaved(resource: Resource) {
        FormHelper.resetForm(this.packageForm);
        this.messageService.openMessage(MessageSavedComponent);

        if (this.publish) {
            this.published.emit(resource);
        } else {
            this.saved.emit(resource);
        }
    }

    private setLoading(loading: boolean) {
        this.resourceBuilder.getService().setLoading(loading);
        this.saving.emit(loading);
    }

    public prepareForm(): Observable<ResourceData> {
        return combineLatest([this.auth.getUser(), this.resourceBuilder.getResource()]).pipe(
            first(),
            map(([user, resource]) => {
                const data = resource
                    ? resource.getData(this.packageForm.value)
                    : new Resource(this.packageForm.value).getData();

                if (!data.id) {
                    data.type_id = ResourceTypes.PACKAGE_E_COURSE;
                    // data.available_types = ResourceTypes.PACKAGE_E_COURSE; // Used for displaying alternative test types for the user.
                    data.customer_id = user.customer_id;
                    data.author_id = user.id;
                }
                if (this.publish) {
                    data.published = moment().format('YYYY-MM-DD HH:mm:ss');
                }
                if (!this.publish && data) {
                    data.last_publish = null;
                }
                return data;
            })
        );
    }
    public showImgModal(id: number) {
        this.dialog.open(DialogDisplayImgComponent, {
            maxWidth: '70%',
            maxHeight: '50vh',
            data: id,
        });
    }

    public canDeactivate(): boolean {
        return this.packageForm.pristine;
    }

    @HostListener('window:beforeunload', ['$event'])
    handleClose($event) {
        $event.returnValue = this.canDeactivate();
    }

    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
